import { handleErrorResponse } from '@frontend/api-client/utils';
import type { RetrieveInviteResponse } from '@frontend/api-types';
import { P, getLocalTz, match } from '@frontend/duck-tape';
import type { AccountCreationFormSchema, AccountCreationSchemaLegacy } from '@frontend/forms';
import { useEffect, useFeatureIsOn, useState } from '@frontend/react';
import { captureSentryException } from '@frontend/web-utils';
import {
  useAnalytics,
  useAuthentication,
  useCreateClientMutation,
  useFrictionlessSignupMutation,
  useLazyListDraftTasksQuery,
} from '@frontend/web/hooks';
import { router } from '@frontend/web/router';
import { handleMutation } from '@frontend/web/utils';

type NavigationParams = {
  search?: {
    description?: string;
    promo?: string;
  };
  to: string;
};

type SignUpFlowConfig = {
  inviteData?: RetrieveInviteResponse;
  navigateOnSuccess?: (params: NavigationParams) => void;
  /**
   * Callback invoked when createClient returns a validation error (code 4001).
   * Used by the legacy wizard flow (flag OFF) to return user to the userInfo step.
   */
  onErrorSetStep?: (step: 'userInfo') => void;
  searchParams?: {
    description?: string;
    promo?: string;
    referrer?: string;
    utm_campaign?: string;
    utm_content?: string;
    utm_medium?: string;
    utm_source?: string;
    utm_term?: string;
  };
  type: 'accept-invite' | 'signup';
};

/**
 * Combined form values type that supports both new and legacy signup flows.
 * - New flow (is_signup_changes_enabled=true): Uses AccountCreationFormSchema (no birthday)
 * - Legacy flow (is_signup_changes_enabled=false): Uses AccountCreationSchemaLegacy (has birthday, phoneNumber required)
 */
type SignUpFormValues = AccountCreationFormSchema | AccountCreationSchemaLegacy;

type UseSignUpFlowReturn = {
  createClientError: ReturnType<typeof useCreateClientMutation>[1]['error'];
  handleSubmitForm: (values: SignUpFormValues) => Promise<unknown>;
  isAnyLoading: boolean;
  isCreateClientLoading: boolean;
  isLoginLoading: boolean;
};

const getNormalizedAnalyticsError = ({
  error,
  errorSource,
}: {
  error: unknown;
  errorSource: string;
}): {
  backendCode?: number;
  errorCategory: 'auth' | 'network' | 'unknown' | 'validation';
  errorCode: string;
  errorSource: string;
  httpStatus?: number;
  retriable: boolean;
} => {
  const errorRecord = typeof error === 'object' && error !== null ? (error as Record<string, unknown>) : undefined;
  const errorData =
    typeof errorRecord?.data === 'object' && errorRecord.data !== null
      ? (errorRecord.data as Record<string, unknown>)
      : undefined;
  const backendCode = typeof errorData?.code === 'number' ? errorData.code : undefined;
  const httpStatus = typeof errorRecord?.status === 'number' ? errorRecord.status : undefined;
  const errorCategory =
    backendCode === 4001
      ? 'validation'
      : httpStatus === 401 || httpStatus === 403
        ? 'auth'
        : typeof httpStatus === 'number' && httpStatus >= 500
          ? 'network'
          : 'unknown';

  return {
    backendCode,
    errorCategory,
    errorCode: backendCode ? `API_${backendCode}` : 'UNKNOWN',
    errorSource,
    httpStatus,
    retriable: backendCode !== 4001,
  };
};

export const useSignUpFlow = (config: SignUpFlowConfig): UseSignUpFlowReturn => {
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const { login } = useAuthentication();
  const [createClient, { error: createClientError, isLoading: isCreateClientLoading }] = useCreateClientMutation();
  const [frictionlessSignup, { error: frictionlessError, isLoading: isFrictionlessLoading }] =
    useFrictionlessSignupMutation();
  const [listDraftTasks] = useLazyListDraftTasksQuery();
  const { track, trackAdvertising } = useAnalytics();
  const { enabled: isSignupChangesEnabled } = useFeatureIsOn('is_signup_changes_enabled');
  const isFrictionlessFlow = config.searchParams?.utm_campaign === 'try-first';
  const flowVariant = isFrictionlessFlow ? 'frictionless' : isSignupChangesEnabled ? 'v2' : 'legacy';

  /**
   * Handle validation errors from createClient API (code 4001).
   * When field validation fails, call onErrorSetStep to return user to first step.
   * This enables the legacy wizard flow to reset to userInfo when validation errors occur.
   */
  useEffect(() => {
    if (createClientError) {
      handleErrorResponse(createClientError, (error) => {
        match<(typeof error)['data']>(error.data)
          .with(
            {
              code: 4001,
              detail: 'Field(s) missing or invalid.',
              errors: P.array({ field: P.string, message: P.string }),
            },
            () => config.onErrorSetStep?.('userInfo'),
          )
          .otherwise(() => undefined);
      });
    }
  }, [createClientError, config.onErrorSetStep]);

  /**
   * Handle form submission for account creation.
   * Payload and navigation differ based on is_signup_changes_enabled flag:
   * - Flag ON (new flow): Omit birthday/phoneNumber, navigate to /onboarding/membership
   * - Flag OFF (legacy flow): Include birthday/phoneNumber, navigate to /app/home
   */
  const handleSubmitForm = async ({ promoCode: signupPromoCode, ...values }: SignUpFormValues) =>
    handleMutation({
      mutation: () =>
        isFrictionlessFlow
          ? frictionlessSignup({
              email: values.email,
              firstName: values.firstName,
              lastName: values.lastName,
              password: values.password,
              ...(config.searchParams?.description ? { taskDescription: config.searchParams.description } : {}),
            })
              .unwrap()
              .then((res) => res.user)
          : createClient({
              bypassOnboarding: Boolean(config.type === 'signup' && config.searchParams?.description),
              email: values.email,
              fallbackToALaCarte: !config.inviteData?.household,
              firstName: values.firstName,
              inviteId: config.inviteData?.id,
              lastName: values.lastName,
              nickname: values.nickname,
              password: values.password,
              primaryTimezone: getLocalTz(),
              referredBy: config.inviteData?.invitedBy || config.searchParams?.referrer,
              // Legacy flow (flag OFF): Include birthday and phoneNumber from form (required fields)
              // New flow (flag ON): Omit these - collected later in /onboarding/profile
              ...(!isSignupChangesEnabled && 'birthday' in values && { birthday: values.birthday }),
              ...(values.phoneNumber && { phoneNumber: values.phoneNumber }),
              signupPromoCode,
            }).unwrap(),
      onError: (error) => {
        track({
          data: {
            ...getNormalizedAnalyticsError({
              error,
              errorSource: 'signupSubmit',
            }),
            entryType: config.type,
            flowVariant,
            hasPromoCode: Boolean(signupPromoCode),
            platform: 'web',
          },
          event: 'signupSubmitFailed',
          topic: 'auth',
        });
      },
      onSuccess: async () => {
        track({
          data: {
            entryType: config.type,
            flowVariant,
            hasPromoCode: Boolean(signupPromoCode),
            platform: 'web',
          },
          event: 'signupSubmitSucceeded',
          topic: 'auth',
        });

        const analyticsData = {
          hasDescription: config.type === 'signup' ? Boolean(config.searchParams?.description) : undefined,
          hasPromoCode: Boolean(signupPromoCode),
          hasReferrer: Boolean(config.inviteData?.invitedBy || config.searchParams?.referrer),
          hasUtmParams: Boolean(
            config.searchParams?.utm_campaign ||
              config.searchParams?.utm_content ||
              config.searchParams?.utm_medium ||
              config.searchParams?.utm_source ||
              config.searchParams?.utm_term,
          ),
          utmCampaign: config.searchParams?.utm_campaign,
          utmContent: config.searchParams?.utm_content,
          utmMedium: config.searchParams?.utm_medium,
          utmSource: config.searchParams?.utm_source,
          utmTerm: config.searchParams?.utm_term,
        };

        track({
          data: analyticsData,
          event: 'signupCompleted',
          topic: 'auth',
        });
        trackAdvertising('CompleteRegistration', analyticsData);

        if (isFrictionlessFlow) {
          track({
            data: {
              entryType: 'frictionless',
              flowVariant: 'frictionless',
              hasTaskDescription: Boolean(config.searchParams?.description),
              method: 'email',
              platform: 'web',
              utmCampaign: config.searchParams?.utm_campaign,
              utmContent: config.searchParams?.utm_content,
              utmMedium: config.searchParams?.utm_medium,
              utmSource: config.searchParams?.utm_source,
            },
            event: 'experimentSignupCompleted',
            topic: 'auth',
          });
        }

        setIsLoginLoading(true);
        try {
          await login({
            bypassInvalidate: true,
            email: values.email,
            handleRedirect: false,
            password: values.password,
          });
          await router.invalidate();
        } finally {
          setIsLoginLoading(false);
        }

        if (config.navigateOnSuccess) {
          if (isFrictionlessFlow) {
            try {
              const draftTasks = await listDraftTasks().unwrap();
              const firstDraft = draftTasks[0];
              if (firstDraft) {
                config.navigateOnSuccess({ to: `/app/draft-task/${firstDraft.id}` });
                return;
              }
            } catch (error) {
              captureSentryException(error);
            }
            config.navigateOnSuccess({ to: '/app/home' });
            return;
          }

          const goToCreateTask = config.type === 'signup' && Boolean(config.searchParams?.description);

          if (goToCreateTask) {
            config.navigateOnSuccess({
              ...(config.searchParams?.description ? { search: { description: config.searchParams.description } } : {}),
              to: '/app/draft-task/create',
            });
          } else {
            const search: NavigationParams['search'] = {
              ...(config.searchParams?.description && { description: config.searchParams.description }),
              ...(config.searchParams?.promo && { promo: config.searchParams.promo }),
            };
            const hasSearchParams = config.searchParams?.description || config.searchParams?.promo;
            // Flag ON (new flow): Household members (invited users) skip membership and go straight to profile.
            // Regular users go to /onboarding/membership to start the membership/payment step.
            // Flag OFF (legacy flow): Navigate to /app/home (birthday/phone already collected during signup)
            const isHouseholdInvite = config.type === 'accept-invite' && Boolean(config.inviteData?.household);
            const postSignupDestination = !isSignupChangesEnabled
              ? '/app/home'
              : isHouseholdInvite
                ? '/onboarding/profile'
                : '/onboarding/membership';
            config.navigateOnSuccess({
              search: hasSearchParams ? search : undefined,
              to: postSignupDestination,
            });
          }
        }
      },
      showSuccessToast: false,
    });

  const trackedHandleSubmitForm = async (values: SignUpFormValues) => {
    track({
      data: {
        entryType: config.type,
        flowVariant,
        hasPromoCode: Boolean(values.promoCode),
        platform: 'web',
      },
      event: 'signupSubmitStarted',
      topic: 'auth',
    });

    return handleSubmitForm(values);
  };

  return {
    createClientError: createClientError || frictionlessError,
    handleSubmitForm: trackedHandleSubmitForm,
    isAnyLoading: isCreateClientLoading || isFrictionlessLoading || isLoginLoading,
    isCreateClientLoading: isCreateClientLoading || isFrictionlessLoading,
    isLoginLoading,
  };
};
